import React from 'react';
import { JSONSchema7 } from 'json-schema';
import { getPrimaryType, isNullable } from '../../../utils/json-schema.util';
import { ArrayField } from 'src/modules/shared/components/molecules/json-schema-form/array-field';
import { ObjectField } from 'src/modules/shared/components/molecules/json-schema-form/object-field';
import { AnyOfField } from 'src/modules/shared/components/molecules/json-schema-form/any-of-field';
import { StringField } from 'src/modules/shared/components/atoms/json-schema-inputs/string-field';
import { NumberField } from 'src/modules/shared/components/atoms/json-schema-inputs/number-field';
import { BooleanField } from 'src/modules/shared/components/atoms/json-schema-inputs/boolean-field';
import {
  AnyOfSelections,
  JSONSchemaFormProps,
} from 'src/modules/shared/types/json-schema.type';
import { FieldWrapper } from 'src/modules/shared/components/molecules/json-schema-form/field-wrapper';
import { EmptySchemaInput } from 'src/modules/shared/components/atoms/json-schema-inputs/emtpy-schema-input';
import { Text } from 'src/modules/shared/components/atoms/text';

interface FieldRendererProps {
  fieldKey: string;
  schema: JSONSchema7;
  fieldSchema: JSONSchema7;
  formData: Record<string, unknown>;
  handleChange: (key: string, value: unknown) => void;
  uiSchema: Record<string, unknown>;
  disabled: boolean;
  allFieldsOptional: boolean;
  anyOfSelections: AnyOfSelections;
  setAnyOfSelections: React.Dispatch<React.SetStateAction<AnyOfSelections>>;
  JSONSchemaForm: React.ComponentType<JSONSchemaFormProps>;
}

export const JSONSchemaField: React.FC<FieldRendererProps> = ({
  fieldKey,
  schema,
  fieldSchema,
  formData,
  handleChange,
  uiSchema,
  disabled,
  allFieldsOptional,
  anyOfSelections,
  setAnyOfSelections,
  JSONSchemaForm,
}) => {
  const fieldTitle = fieldSchema.title || fieldKey;
  const fieldDescription = fieldSchema.description;
  const isRequired = allFieldsOptional
    ? false
    : schema.required?.includes(fieldKey) || false;
  const fieldUiSchema = uiSchema[fieldKey] || {};
  const fieldValue = formData[fieldKey];

  const isEmptySchema = Object.keys(fieldSchema).length === 0;
  if (isEmptySchema) {
    return (
      <FieldWrapper
        fieldKey={fieldKey}
        fieldTitle={fieldTitle}
        fieldDescription={fieldDescription}
        isRequired={isRequired}
      >
        <EmptySchemaInput
          id={`field-${fieldKey}`}
          schema={fieldSchema}
          value={fieldValue}
          onChange={(value) => handleChange(fieldKey, value)}
          disabled={disabled}
        />
      </FieldWrapper>
    );
  }

  const hasAnyOfSchemas =
    fieldSchema.anyOf &&
    Array.isArray(fieldSchema.anyOf) &&
    fieldSchema.anyOf.length > 0;
  if (hasAnyOfSchemas) {
    return (
      <FieldWrapper
        fieldKey={fieldKey}
        fieldTitle={fieldTitle}
        fieldDescription={fieldDescription}
        isRequired={isRequired}
      >
        <AnyOfField
          fieldKey={fieldKey}
          schema={fieldSchema}
          value={fieldValue}
          handleChange={handleChange}
          anyOfSelections={anyOfSelections}
          setAnyOfSelections={setAnyOfSelections}
          disabled={disabled}
          allFieldsOptional={allFieldsOptional}
          JSONSchemaForm={JSONSchemaForm}
        />
      </FieldWrapper>
    );
  }

  const primaryType = getPrimaryType(fieldSchema);
  const hasAdditionalProperties =
    primaryType === 'object' && fieldSchema.additionalProperties;
  if (hasAdditionalProperties) {
    return (
      <FieldWrapper
        fieldKey={fieldKey}
        fieldTitle={fieldTitle}
        fieldDescription={fieldDescription}
        isRequired={isRequired}
      >
        <ObjectField
          fieldKey={fieldKey}
          schema={fieldSchema}
          value={(fieldValue as Record<string, unknown>) || {}}
          handleChange={handleChange}
          disabled={disabled}
          JSONSchemaForm={JSONSchemaForm}
        />
      </FieldWrapper>
    );
  }

  const nullable = isNullable(fieldSchema);
  switch (primaryType) {
    case 'string': {
      return (
        <FieldWrapper
          fieldKey={fieldKey}
          fieldTitle={fieldTitle}
          fieldDescription={fieldDescription}
          isRequired={isRequired}
        >
          <StringField
            id={`field-${fieldKey}`}
            schema={fieldSchema}
            value={fieldValue}
            onChange={(value) => handleChange(fieldKey, value)}
            disabled={disabled}
            nullable={nullable}
            placeholder=""
            uiSchema={fieldUiSchema as Record<string, unknown>}
          />
        </FieldWrapper>
      );
    }
    case 'number':
    case 'integer': {
      return (
        <FieldWrapper
          fieldKey={fieldKey}
          fieldTitle={fieldTitle}
          fieldDescription={fieldDescription}
          isRequired={isRequired}
        >
          <NumberField
            id={`field-${fieldKey}`}
            schema={fieldSchema}
            value={fieldValue}
            onChange={(value) => handleChange(fieldKey, value)}
            disabled={disabled}
            nullable={nullable}
            fieldType={primaryType}
          />
        </FieldWrapper>
      );
    }
    case 'boolean': {
      return (
        <FieldWrapper
          fieldKey={fieldKey}
          fieldTitle={fieldTitle}
          fieldDescription={fieldDescription}
          isRequired={isRequired}
        >
          <BooleanField
            id={`field-${fieldKey}`}
            schema={fieldSchema}
            value={fieldValue}
            onChange={(value) => handleChange(fieldKey, value)}
            disabled={disabled}
            nullable={nullable}
            label={nullable ? undefined : fieldTitle}
          />
        </FieldWrapper>
      );
    }
    case 'object': {
      if (!fieldSchema.properties) break;

      return (
        <FieldWrapper
          fieldKey={fieldKey}
          fieldTitle={fieldTitle}
          fieldDescription={fieldDescription}
          isRequired={isRequired}
        >
          <ObjectField
            fieldKey={fieldKey}
            schema={fieldSchema}
            value={(fieldValue as Record<string, unknown>) || {}}
            handleChange={handleChange}
            disabled={disabled}
            allFieldsOptional={allFieldsOptional}
            title={fieldTitle}
            JSONSchemaForm={JSONSchemaForm}
          />
        </FieldWrapper>
      );
    }
    case 'array': {
      if (!fieldSchema.items) break;

      return (
        <FieldWrapper
          fieldKey={fieldKey}
          fieldTitle={fieldTitle}
          fieldDescription={fieldDescription}
          isRequired={isRequired}
        >
          <ArrayField
            fieldKey={fieldKey}
            schema={fieldSchema}
            value={(fieldValue as unknown[]) || []}
            handleChange={handleChange}
            disabled={disabled}
            allFieldsOptional={allFieldsOptional}
            JSONSchemaForm={JSONSchemaForm}
          />
        </FieldWrapper>
      );
    }
    default: {
      return (
        <FieldWrapper
          fieldKey={fieldKey}
          fieldTitle={fieldTitle}
          fieldDescription={fieldDescription}
          isRequired={isRequired}
        >
          <Text size="small" color="neutral">
            {/* TODO: check when implementing i18n */}
            Tipo no soportado: {primaryType}
          </Text>
        </FieldWrapper>
      );
    }
  }

  return null;
};
